/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * backoffice-api
 * Staff-facing BFF: login, staff/casino administration, kiosk onboarding. Orchestrates play-svc and device-svc over x-internal-key; never calls wallet-svc (S5) and never calls device-api — a BFF does not call another BFF (ADR-0001).
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from "@tanstack/vue-query";
import type {
  DataTag,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationReturnType,
  UseQueryOptions,
  UseQueryReturnType,
} from "@tanstack/vue-query";

import { unref } from "vue";
import type { MaybeRef } from "vue";

import type {
  BackofficeLogin200,
  BackofficeLogin400,
  BackofficeLogin401,
  BackofficeLogin429,
  BackofficeLogin502,
  BackofficeLoginBody,
  BackofficeWhoami200,
  BackofficeWhoami401,
  BackofficeWhoami403,
  BackofficeWhoami502,
} from "../../model";

import { apiFetch } from "../../../shared/api-fetch";
import type { ErrorType } from "../../../shared/api-fetch";

/**
 * The only unauthenticated route on this service and the only place in the platform where a human-chosen secret is checked. play-svc verifies the password and mints the token, so the hash never leaves the service that owns it (S4); the token carries `{staffId}` and grants nothing on its own — `role` and `casinoId` come back here for the UI to render and are re-resolved on every later request, so a demotion, a deactivation or a Casino suspension takes effect at once rather than at the end of a TTL. Attempt limiting is per-username AND per-source-IP with exponential backoff (S7), checked BEFORE play-svc is called; a wrong password, an unknown username and a deactivated account answer identically, and in constant work, so neither the response nor its timing is an oracle for which usernames exist.
 * @summary Exchange a staff username and password for a staff identity token
 */
export const backofficeLogin = (
  backofficeLoginBody: MaybeRef<BackofficeLoginBody>,
  signal?: AbortSignal,
) => {
  backofficeLoginBody = unref(backofficeLoginBody);

  return apiFetch<BackofficeLogin200>({
    url: `/v1/login`,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    data: backofficeLoginBody,
    ...(signal ? { signal } : {}),
  });
};

export const getBackofficeLoginMutationOptions = <
  TError = ErrorType<
    BackofficeLogin400 | BackofficeLogin401 | BackofficeLogin429 | BackofficeLogin502
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof backofficeLogin>>,
    TError,
    { data: BackofficeLoginBody },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof backofficeLogin>>,
  TError,
  { data: BackofficeLoginBody },
  TContext
> => {
  const mutationKey = ["backofficeLogin"];
  const { mutation: mutationOptions } = options
    ? options.mutation && "mutationKey" in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof backofficeLogin>>,
    { data: BackofficeLoginBody }
  > = (props) => {
    const { data } = props ?? {};

    return backofficeLogin(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type BackofficeLoginMutationResult = NonNullable<
  Awaited<ReturnType<typeof backofficeLogin>>
>;
export type BackofficeLoginMutationBody = BackofficeLoginBody;
export type BackofficeLoginMutationError = ErrorType<
  BackofficeLogin400 | BackofficeLogin401 | BackofficeLogin429 | BackofficeLogin502
>;

/**
 * @summary Exchange a staff username and password for a staff identity token
 */
export const useBackofficeLogin = <
  TError = ErrorType<
    BackofficeLogin400 | BackofficeLogin401 | BackofficeLogin429 | BackofficeLogin502
  >,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof backofficeLogin>>,
      TError,
      { data: BackofficeLoginBody },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationReturnType<
  Awaited<ReturnType<typeof backofficeLogin>>,
  TError,
  { data: BackofficeLoginBody },
  TContext
> => {
  const mutationOptions = getBackofficeLoginMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Any authenticated role. `role` and `casinoId` are the ones this request's guards were decided against (S4), not a second read that could already disagree with them.
 * @summary The signed-in staff member, as authorization currently resolves them
 */
export const backofficeWhoami = (signal?: AbortSignal) => {
  return apiFetch<BackofficeWhoami200>({
    url: `/v1/me`,
    method: "GET",
    ...(signal ? { signal } : {}),
  });
};

export const getBackofficeWhoamiQueryKey = () => {
  return ["v1", "me"] as const;
};

export const getBackofficeWhoamiQueryOptions = <
  TData = Awaited<ReturnType<typeof backofficeWhoami>>,
  TError = ErrorType<BackofficeWhoami401 | BackofficeWhoami403 | BackofficeWhoami502>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backofficeWhoami>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = getBackofficeWhoamiQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof backofficeWhoami>>> = ({ signal }) =>
    backofficeWhoami(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof backofficeWhoami>>,
    TError,
    TData
  >;
};

export type BackofficeWhoamiQueryResult = NonNullable<Awaited<ReturnType<typeof backofficeWhoami>>>;
export type BackofficeWhoamiQueryError = ErrorType<
  BackofficeWhoami401 | BackofficeWhoami403 | BackofficeWhoami502
>;

/**
 * @summary The signed-in staff member, as authorization currently resolves them
 */

export function useBackofficeWhoami<
  TData = Awaited<ReturnType<typeof backofficeWhoami>>,
  TError = ErrorType<BackofficeWhoami401 | BackofficeWhoami403 | BackofficeWhoami502>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof backofficeWhoami>>, TError, TData>>;
  },
  queryClient?: QueryClient,
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBackofficeWhoamiQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>;

  return query;
}
